export const getSupportedRecorderMimeType = (withAudio = true): string => {
  const mimeTypes = withAudio
    ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
    : ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];

  return mimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? 'video/webm';
};

export const stopMediaStream = (stream: MediaStream | null): void => {
  stream?.getTracks().forEach((track) => track.stop());
};

export const stopMediaRecorder = (recorder: MediaRecorder | null): Promise<void> => {
  if (!recorder || recorder.state === 'inactive') return Promise.resolve();

  return new Promise((resolve) => {
    const timeout = window.setTimeout(resolve, 5_000);
    recorder.addEventListener(
      'stop',
      () => {
        window.clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
    recorder.stop();
  });
};

export const formatRecordingDuration = (durationSeconds: number): string => {
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export type MixedRecordingStream = {
  audioContext: AudioContext | null;
  stream: MediaStream;
};

export const createMixedRecordingStream = async (
  displayStream: MediaStream,
  microphoneStream: MediaStream | null,
): Promise<MixedRecordingStream> => {
  const stream = new MediaStream(displayStream.getVideoTracks());
  const audioStreams = [displayStream, microphoneStream].filter(
    (candidate): candidate is MediaStream => Boolean(candidate?.getAudioTracks().length),
  );

  if (audioStreams.length === 0) return { audioContext: null, stream };

  const audioContext = new AudioContext();
  await audioContext.resume();
  const destination = audioContext.createMediaStreamDestination();

  audioStreams.forEach((audioStream) => {
    audioContext.createMediaStreamSource(audioStream).connect(destination);
  });

  destination.stream.getAudioTracks().forEach((track) => stream.addTrack(track));

  return { audioContext, stream };
};
